/**
 * useMediaQuery Hook
 * Custom hook for responsive media query detection
 */

'use client';

import { useCallback, useSyncExternalStore } from 'react';

const DEFAULT_MEDIA_QUERY_MATCH = false;

function getServerSnapshot(): boolean {
  return DEFAULT_MEDIA_QUERY_MATCH;
}

/**
 * Hook to check if a media query matches.
 *
 * @param query - Media query string (e.g., '(min-width: 768px)')
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((onStoreChange: () => void): (() => void) => {
    const mediaQuery = window.matchMedia(query);

    mediaQuery.addEventListener('change', onStoreChange);
    return () => mediaQuery.removeEventListener('change', onStoreChange);
  }, [query]);

  const getSnapshot = useCallback((): boolean => {
    if (typeof window === 'undefined') {
      return DEFAULT_MEDIA_QUERY_MATCH;
    }

    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
